import React, { useState, useEffect } from "react";
import { createTicket, fetchApi } from "../../utils/api";
import "../../style/user/ContactSupport.css";

export default function ContactSupport() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    problem: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    const loadContacts = async () => {
      try {
        const data = await fetchApi("/support");
        setContacts(data || []);
      } catch (err) {
        console.error("Failed to load contacts:", err);
      }
    };
    loadContacts();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    const ticketData = {
      subject: form.problem,
      message: form.problem,
      email: form.email,
      name: form.fullName,
    };

    try {
      const response = await createTicket(ticketData);
      if (response.success) {
        setSuccessMessage("Your ticket has been submitted successfully!");
        setForm({ fullName: "", email: "", problem: "" });
      }
    } catch (err) {
      setError("There was an error submitting your ticket.");
      console.error("Ticket creation error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk menghasilkan link berdasarkan tipe kontak
  const generateContactLink = (contact) => {
    const { type, value } = contact;
    
    switch (type) {
      case "email":
        return `mailto:${value}`;
      
      case "whatsapp":
        // Hapus semua karakter non-digit untuk nomor WhatsApp
        const cleanNumber = value.replace(/\D/g, '');
        return `https://wa.me/${cleanNumber}`;
      
      case "telegram":
        // Handle berbagai format username Telegram
        let telegramUsername = value.replace(/[@\s]/g, '');
        if (telegramUsername.startsWith('https://t.me/')) {
          return telegramUsername;
        } else {
          return `https://t.me/${telegramUsername}`;
        }
      
      case "facebook":
        // Handle berbagai format Facebook
        if (value.includes('facebook.com') || value.includes('fb.com')) {
          return value;
        } else {
          // Jika hanya username, asumsikan facebook.com/username
          const fbUsername = value.replace(/[@\s]/g, '');
          return `https://facebook.com/${fbUsername}`;
        }
      
      case "instagram":
        // Handle berbagai format Instagram
        if (value.includes('instagram.com')) {
          return value;
        } else {
          const igUsername = value.replace(/[@\s]/g, '');
          return `https://instagram.com/${igUsername}`;
        }
      
      case "twitter":
      case "x":
        // Handle berbagai format Twitter/X
        if (value.includes('twitter.com') || value.includes('x.com')) {
          return value;
        } else {
          const twUsername = value.replace(/[@\s]/g, '');
          return `https://twitter.com/${twUsername}`;
        }
      
      default:
        // Jika tipe tidak dikenali, gunakan value apa adanya
        return value;
    }
  };

  // Fungsi untuk mendapatkan icon/tambahan informasi berdasarkan tipe
  const getLinkAttributes = (type) => {
    const attributes = {
      target: "_blank",
      rel: "noopener noreferrer"
    };
    
    // Tambahkan atribut khusus untuk email
    if (type === "email") {
      return {
        ...attributes,
        // Email link tidak perlu target blank
        target: "_self"
      };
    }
    
    return attributes;
  };

  return (
    <div className="contact-support-container">
      <h1 className="page-titles">Contact Support</h1>

      <div className="contact-grid">
        {/* Left Form */}
        <div className="support-card">
          <h3 className="section-title">Tell Your Problem</h3>

          <form onSubmit={handleSubmit} className="support-form">
            <div className="form-group">
              <label>Full Name <span>*</span></label>
              <input
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                placeholder="fill name"
                required
              />
            </div>

            <div className="form-group">
              <label>Email <span>*</span></label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="ex:tioramdan@gmail.com"
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Problem <span>*</span></label>
              <textarea
                name="problem"
                placeholder="Type problem description here..."
                maxLength="2000"
                value={form.problem}
                onChange={handleChange}
                required
              ></textarea>
              <small>Max. 2,000 characters</small>
            </div>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <button type="submit" className="btn-send" disabled={loading}>
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>

        {/* Right Contact Info */}
        <div className="contact-info">
          {contacts
            .filter(c => c.is_active === 1)
            .map((contact) => {
              const linkHref = generateContactLink(contact);
              const linkAttributes = getLinkAttributes(contact.type);
              
              return (
                <div className="info-box" key={contact.id}>
                  <h4>{contact.label}</h4>
                  {contact.description && <p>{contact.description}</p>}
                  <a
                    href={linkHref}
                    className="info-link"
                    {...linkAttributes}
                  >
                    {/* Tampilkan icon berdasarkan tipe */}
                    <span className="contact-icon">
                      {contact.type === "email"}
                      {contact.type === "whatsapp"}
                      {contact.type === "telegram"}
                      {contact.type === "facebook"}
                      {contact.type === "instagram"}
                      {contact.type === "twitter"}
                      {contact.type === "x"}
                    </span>
                    {contact.value} ↗
                  </a>
                </div>
              );
            })}
          
          {/* Tampilkan pesan jika tidak ada kontak aktif */}
          {contacts.filter(c => c.is_active === 1).length === 0 && (
            <div className="info-box no-contacts">
              <p>No contact information available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}